import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import "bulma/css/bulma.min.css";
import "../../../styles/calculocostos.css";
import { useEmpresaSelector } from '../../../context/EmpresaSelectorContext';
import { useAuth } from '../../../context/AuthContext';
import { apiRequest } from '../../../utils/api';
import * as XLSX from 'xlsx';

const num = v => {
  const n = typeof v === 'string' ? v.replace(',', '.') : v;
  const parsed = parseFloat(n);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundToFour = (num) => Math.round(num * 100) / 100; // Redondeo a 2 decimales para consistencia
const roundToTwo = (num) => Math.round(num * 100) / 100; // Redondeo a 2 decimales

const defaultInventario = [
  {
    id: 1,
    numeroArticulo: '',
    nombreArticulo: '',
    distribuidor: '',
    unidad: '',
    stockTotal: 0,
    salida: 0,
    stockFinal: 0,
    costoUnitario: 0,
    iva: 0,
    precioUnitario: 0,
    precioStockTotal: 0,
    precioStockFinal: 0,
  },
];

const CalculoProductos = () => {
  const [inventario, setInventario] = useState(defaultInventario);
  const [productosInventario, setProductosInventario] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [editingProductId, setEditingProductId] = useState(null);
  const { empresaSeleccionada } = useEmpresaSelector();
  const empresaId = (empresaSeleccionada && empresaSeleccionada.id) || 'default';
  const { usuario, loading } = useAuth();
  const allowedRoles = ['conexadmin', 'administrador', 'trabajadores'];
  if (loading) return <div className="notification is-info">Cargando usuario...</div>;
  if (!usuario || !allowedRoles.includes(usuario.rol)) {
    return <div className="notification is-danger">Acceso denegado. No tienes permisos para ver este módulo.</div>;
  }

  // Estados por empresa
  const [datosPorEmpresa, setDatosPorEmpresa] = useState({});

  // Inicializar datos si no existen para la empresa
  useEffect(() => {
    setDatosPorEmpresa(prev => {
      const existing = prev[empresaId];
      if (existing && Array.isArray(existing.costosDirectos) && Array.isArray(existing.manoObra) && Array.isArray(existing.costosFijos) && Array.isArray(existing.costosVariables)) return prev;
      return {
        ...prev,
        [empresaId]: {
          costosDirectos: Array.isArray(existing && existing.costosDirectos) ? existing.costosDirectos : [{ id: 1, nombreProducto: '', nombreArticulo: '', cantidad: 0, unidad: '', costoUnitario: 0, costoTotal: 0 }],
          manoObra: Array.isArray(existing && existing.manoObra) ? existing.manoObra : [{ id: 1, personal: '', horas: 0, costoHora: 0, costoTotal: 0 }],
          costosFijos: Array.isArray(existing && existing.costosFijos) ? existing.costosFijos : [{ id: 1, concepto: '', costoMensual: 0 }],
          costosVariables: Array.isArray(existing && existing.costosVariables) ? existing.costosVariables : [{ id: 1, concepto: '', costoMensual: 0 }],
          utilidad: (existing && existing.utilidad) || '',
          iva: (existing && existing.iva) || '',
        }
      };
    });
  }, [empresaId]);

  // Helpers para acceder y setear los datos de la empresa activa
  const costosDirectos = (() => {
    const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].costosDirectos;
    const array = Array.isArray(raw) ? raw : [];
    return array.filter(item => item !== null && item !== undefined);
  })();
  const setCostosDirectos = (updater) => setDatosPorEmpresa(prev => {
    const current = (prev[empresaId] && prev[empresaId].costosDirectos) || [];
    const newArr = typeof updater === 'function' ? updater(current) : updater;
    return { ...prev, [empresaId]: { ...prev[empresaId], costosDirectos: newArr } };
  });
  const manoObra = (() => {
    const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].manoObra;
    const array = Array.isArray(raw) ? raw : [];
    return array.filter(item => item !== null && item !== undefined);
  })();
  const setManoObra = (updater) => setDatosPorEmpresa(prev => {
    const current = (prev[empresaId] && prev[empresaId].manoObra) || [];
    const newArr = typeof updater === 'function' ? updater(current) : updater;
    return { ...prev, [empresaId]: { ...prev[empresaId], manoObra: newArr } };
  });
  const costosFijos = (() => {
    const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].costosFijos;
    const array = Array.isArray(raw) ? raw : [];
    return array.filter(item => item !== null && item !== undefined);
  })();
  const setCostosFijos = (updater) => setDatosPorEmpresa(prev => {
    const current = (prev[empresaId] && prev[empresaId].costosFijos) || [];
    const newArr = typeof updater === 'function' ? updater(current) : updater;
    return { ...prev, [empresaId]: { ...prev[empresaId], costosFijos: newArr } };
  });
  const costosVariables = (() => {
    const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].costosVariables;
    const array = Array.isArray(raw) ? raw : [];
    return array.filter(item => item !== null && item !== undefined);
  })();
  const setCostosVariables = (updater) => setDatosPorEmpresa(prev => {
    const current = (prev[empresaId] && prev[empresaId].costosVariables) || [];
    const newArr = typeof updater === 'function' ? updater(current) : updater;
    return { ...prev, [empresaId]: { ...prev[empresaId], costosVariables: newArr } };
  });
  const utilidad = (datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].utilidad) || 0;
  const setUtilidad = (val) => setDatosPorEmpresa(prev => ({ ...prev, [empresaId]: { ...prev[empresaId], utilidad: val } }));
  const iva = (datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].iva) || 0;
  const setIva = (val) => setDatosPorEmpresa(prev => ({ ...prev, [empresaId]: { ...prev[empresaId], iva: val } }));

  // Filtros de fecha
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');

  // Función para cargar productos filtrados
  const fetchProductosFiltrados = async (limpiar = false) => {
    let endpoint = `/productos-calculados`;
    if (empresaId !== 'default') endpoint += `?empresaId=${empresaId}`;
    if (!limpiar) {
      const separator = endpoint.includes('?') ? '&' : '?';
      if (filtroFechaInicio) endpoint += `${separator}fechaInicio=${filtroFechaInicio}`;
      if (filtroFechaFin) endpoint += `${endpoint.includes('?') ? '&' : '?'}fechaFin=${filtroFechaFin}`;
    }
    try {
      const res = await apiRequest(endpoint);
      if (res.ok) {
        const productos = await res.json();
        const productosGuardados = productos.filter(p => p != null && p.nombre).map(p => ({
          id: p.id,
          nombreProducto: p.nombre,
          stockFinal: (p.detalles && p.detalles.inventario && p.detalles.inventario[0] && p.detalles.inventario[0].stockFinal) || 0,
          total: p.total,
          fecha: p.fecha,
          detalles: p.detalles,
        }));
        setDatosPorEmpresa(prev => ({
          ...prev,
          [empresaId]: {
            ...prev[empresaId],
            productosGuardados
          }
        }));
      }
    } catch { }
  };

  // Cargar productos calculados del backend al montar/cambiar empresa
  useEffect(() => {
    fetchProductosFiltrados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Cargar productos del inventario desde el backend
  useEffect(() => {
    const cargarProductosInventario = async () => {
      if (!empresaSeleccionada || !empresaSeleccionada.id) {
        setLoadingProductos(false);
        return;
      }

      setLoadingProductos(true);
      try {
        let endpoint = '/inventario';
        if (empresaSeleccionada.id !== 'all') {
          endpoint += `?empresaId=${empresaSeleccionada.id}`;
        }

        const response = await apiRequest(endpoint);
        if (response.ok) {
          const data = await response.json();
          setProductosInventario(Array.isArray(data) ? data : []);
        } else {
          setProductosInventario([]);
        }
      } catch (error) {
        console.error('Error cargando productos del inventario:', error);
        setProductosInventario([]);
      } finally {
        setLoadingProductos(false);
      }
    };

    cargarProductosInventario();
  }, [empresaSeleccionada && empresaSeleccionada.id]); // Cambiar dependencia para evitar problemas

  // Manejar cambios en la tabla Inventario
  const handleInventarioChange = (id, field, value) => {
    // Mejor manejo de valores numéricos pequeños
    let processedValue = value;
    if (typeof processedValue === 'string') {
      // Manejar punto decimal al inicio
      if (processedValue === '.') {
        processedValue = '0.';
      }
      // Asegurar que los números sean parseados correctamente
      if (['costoUnitario', 'iva', 'stockTotal', 'salida'].includes(field)) {
        // Reemplazar comas por puntos para el parseo correcto
        processedValue = processedValue.replace(',', '.');
        // Si el valor es un número válido, convertirlo a número
        if (!isNaN(parseFloat(processedValue)) && isFinite(processedValue)) {
          processedValue = parseFloat(processedValue);
        } else {
          // Si no es un número válido, mantener el valor anterior
          const currentItem = inventario.find(item => item.id === id);
          if (currentItem) {
            processedValue = currentItem[field];
          } else {
            processedValue = 0;
          }
        }
      }
    }
    setInventario(prev => prev.filter(item => item).map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: processedValue };
      // IVA editable por fila
      updated.iva = field === 'iva' ? processedValue : item.iva;

      // Cálculos automáticos con manejo de decimales pequeños
      const costoUnitario = parseFloat(updated.costoUnitario) || 0;
      const ivaPorcentaje = parseFloat(updated.iva) || 0;
      const stockTotal = parseFloat(updated.stockTotal) || 0;
      const salida = parseFloat(updated.salida) || 0;

      // Calcular precio unitario con IVA
      const ivaMonto = (costoUnitario * ivaPorcentaje) / 100;
      updated.precioUnitario = roundToFour(costoUnitario + ivaMonto);

      // Calcular stock final
      updated.stockFinal = roundToFour(stockTotal - salida);

      // Calcular precios totales
      updated.precioStockTotal = roundToFour(updated.precioUnitario * stockTotal);
      updated.precioStockFinal = roundToFour(updated.precioUnitario * updated.stockFinal);

      return updated;
    }));

    // Sincronizar cantidad en costos directos cuando cambia salida en inventario
    if (field === 'salida') {
      setCostosDirectos(prev => prev.map(item => {
        if (item.nombreArticulo === inventario.find(inv => inv.id === id)?.nombreArticulo) {
          const updatedItem = { ...item, cantidad: processedValue };
          updatedItem.costoTotal = roundToFour(num(updatedItem.costoUnitario) * num(updatedItem.cantidad));
          return updatedItem;
        }
        return item;
      }));
    }
  };


  // Manejar cambios en la tabla Costos Directos
  const handleDirectoChange = (id, field, value) => {
    // Procesar value para convertir '.' a '0.'
    let processedValue = value;
    if (typeof processedValue === 'string' && processedValue === '.') {
      processedValue = '0.';
    }
    setCostosDirectos(prev => prev.filter(item => item).map(item => {
      if (item.id !== id) return item;
      let updated = { ...item, [field]: processedValue };
      // Si cambia el artículo, actualizar cantidad, costo unitario y unidad desde inventario local
      if (field === 'nombreArticulo') {
        const inv = inventario.find(i => i.nombreArticulo === processedValue);
        if (inv) {
          updated.cantidad = num(inv.salida) || 0;
          updated.costoUnitario = num(inv.costoUnitario) || 0;
          updated.unidad = inv.unidad || '';
        } else {
          // Si no se encuentra el producto, limpiar los valores
          updated.cantidad = 0;
          updated.costoUnitario = 0;
          updated.unidad = '';
        }
      }
      // Si cambia la cantidad, sincronizar con salida en inventario y recalcular costo total
      if (field === 'cantidad') {
        setInventario(prev => prev.map(inv => {
          if (inv.nombreArticulo === updated.nombreArticulo) {
            const updatedInv = { ...inv, salida: processedValue };
            updatedInv.stockFinal = roundToFour(num(updatedInv.stockTotal) - num(updatedInv.salida));
            updatedInv.precioUnitario = roundToFour(num(updatedInv.costoUnitario) + (num(updatedInv.costoUnitario) * num(updatedInv.iva) / 100));
            updatedInv.precioStockTotal = roundToFour(updatedInv.precioUnitario * num(updatedInv.stockTotal));
            updatedInv.precioStockFinal = roundToFour(updatedInv.precioUnitario * num(updatedInv.stockFinal));
            return updatedInv;
          }
          return inv;
        }));
      }
      // Calcular costo total
      updated.costoTotal = roundToFour(num(updated.costoUnitario) * num(updated.cantidad));
      return updated;
    }));
  };

  const addInventarioRow = () => {
    const newId = Math.max(0, ...inventario.map(i => i.id)) + 1;
    setInventario(prev => [
      ...prev,
      {
        id: newId,
        numeroArticulo: '',
        nombreArticulo: '',
        distribuidor: '',
        unidad: '',
        stockTotal: 0,
        salida: 0,
        stockFinal: 0,
        costoUnitario: 0,
        iva: 0,
        precioUnitario: 0,
        precioStockTotal: 0,
        precioStockFinal: 0,
      },
    ]);
  };

  const removeInventarioRow = (id) => {
    setInventario(prev => prev.filter(item => item.id !== id));
  };

  const addDirectoRow = () => {
    const newId = Math.max(0, ...costosDirectos.map(i => i.id)) + 1;
    setCostosDirectos(prev => [
      ...prev,
      {
        id: newId,
        nombreProducto: '',
        nombreArticulo: '',
        cantidad: 0,
        unidad: '',
        costoUnitario: 0,
        costoTotal: 0,
      },
    ]);
  };

  const removeDirectoRow = (id) => {
    setCostosDirectos(prev => prev.filter(item => item.id !== id));
  };

  // Mano de obra
  const handleManoObraChange = (id, field, value) => {
    // Procesar value para convertir '.' a '0.'
    let processedValue = value;
    if (typeof processedValue === 'string' && processedValue === '.') {
      processedValue = '0.';
    }
    setManoObra(prev => prev.filter(item => item).map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: processedValue };
      updated.costoTotal = roundToFour(num(updated.horas) * num(updated.costoHora));
      return updated;
    }));
  };

  const addManoObraRow = () => {
    const newId = Math.max(0, ...manoObra.map(i => i.id)) + 1;
    setManoObra(prev => [
      ...prev,
      {
        id: newId,
        personal: '',
        horas: 0,
        costoHora: 0,
        costoTotal: 0,
      },
    ]);
  };

  const removeManoObraRow = (id) => {
    setManoObra(prev => prev.filter(item => item.id !== id));
  };

  // Costos fijos
  const handleFijoChange = (id, field, value) => {
    // Procesar value para convertir '.' a '0.'
    let processedValue = value;
    if (typeof processedValue === 'string' && processedValue === '.') {
      processedValue = '0.';
    }
    setCostosFijos(prev => prev.filter(item => item).map(item => item.id === id ? { ...item, [field]: processedValue } : item));
  };
  const addFijoRow = () => {
    const newId = Math.max(0, ...costosFijos.map(i => i.id)) + 1;
    setCostosFijos(prev => [...prev, { id: newId, concepto: '', costoMensual: 0 }]);
  };
  const removeFijoRow = (id) => {
    setCostosFijos(prev => prev.filter(item => item.id !== id));
  };

  // Costos variables
  const handleVariableChange = (id, field, value) => {
    // Procesar value para convertir '.' a '0.'
    let processedValue = value;
    if (typeof processedValue === 'string' && processedValue === '.') {
      processedValue = '0.';
    }
    setCostosVariables(prev => prev.filter(item => item).map(item => item.id === id ? { ...item, [field]: processedValue } : item));
  };
  const addVariableRow = () => {
    const newId = Math.max(0, ...costosVariables.map(i => i.id)) + 1;
    setCostosVariables(prev => [...prev, { id: newId, concepto: '', costoMensual: 0 }]);
  };
  const removeVariableRow = (id) => {
    setCostosVariables(prev => prev.filter(item => item.id !== id));
  };

  // Cálculos
  const costoUnitario = () => {
    const totalDirectos = costosDirectos.reduce((sum, item) => sum + num(item.costoTotal), 0);
    const totalManoObra = manoObra.reduce((sum, item) => sum + num(item.costoTotal), 0);
    return roundToFour(totalDirectos + totalManoObra);
  };
  const costosOperativos = () => {
    const totalFijos = costosFijos.reduce((sum, item) => sum + num(item.costoMensual), 0);
    const totalVariables = costosVariables.reduce((sum, item) => sum + num(item.costoMensual), 0);
    // Redondear cada total a 4 decimales (no al inmediato superior)
    const totalFijosRounded = roundToFour(totalFijos);
    const totalVariablesRounded = roundToFour(totalVariables);
    return roundToFour(totalFijosRounded + totalVariablesRounded);
  };
  const subtotal = () => {
    const base = costoUnitario() + costosOperativos();
    return roundToFour(base + (base * num(utilidad) / 100));
  };
  const ivaMonto = () => roundToFour(subtotal() * num(iva) / 100);
  const total = () => roundToTwo(subtotal() + ivaMonto());

  // Permitir agregar productos ilimitados

  const productosGuardados = (() => {
    const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].productosGuardados;
    const array = Array.isArray(raw) ? raw : [];
    return array.filter(p => p !== null && p !== undefined);
  })();

  return (
    <div className="calculocostos-container">
      <h1 className="calculocostos-title">Cálculo de Productos</h1>
      {/* Botón Excel para todos los datos */}
      <div className="has-text-centered mt-4 mb-4">
        <button
          className="button is-link is-medium mr-2"
          onClick={async () => {
            const wb = XLSX.utils.book_new();

            // Inventario (usando datos locales, no del backend)
            const sheetInventario = inventario.map((item, idx) => ({
              '#': idx + 1,
              'N° de artículo': item.numeroArticulo,
              'Nombre del artículo': item.nombreArticulo,
              Distribuidor: item.distribuidor,
              Unidad: item.unidad,
              'Stock total': item.stockTotal,
              Salida: item.salida,
              'Stock final': item.stockFinal,
              'Costo unitario': item.costoUnitario,
              'IVA (%)': item.iva,
              'Precio unitario': item.precioUnitario,
              'Precio stock total': item.precioStockTotal,
              'Precio stock final': item.precioStockFinal,
            }));
            const wsInventario = XLSX.utils.json_to_sheet(sheetInventario);
            XLSX.utils.book_append_sheet(wb, wsInventario, 'Inventario');

            // Costos Directos
            const sheetDirectos = costosDirectos.map((item, idx) => ({
              '#': idx + 1,
              'Nombre del producto': item.nombreProducto,
              'Nombre del artículo': item.nombreArticulo,
              Cantidad: item.cantidad,
              Unidad: item.unidad,
              'Costo unitario': item.costoUnitario,
              'Costo total': item.costoTotal,
            }));
            const wsDirectos = XLSX.utils.json_to_sheet(sheetDirectos);
            XLSX.utils.book_append_sheet(wb, wsDirectos, 'Costos Directos');

            // Mano de Obra
            const sheetManoObra = manoObra.map((item, idx) => ({
              '#': idx + 1,
              Personal: item.personal,
              Horas: item.horas,
              'Costo/Hora': item.costoHora,
              'Costo Total': item.costoTotal,
            }));
            const wsManoObra = XLSX.utils.json_to_sheet(sheetManoObra);
            XLSX.utils.book_append_sheet(wb, wsManoObra, 'Mano de Obra');

            // Costos Fijos
            const sheetFijos = costosFijos.map((item, idx) => ({
              '#': idx + 1,
              Concepto: item.concepto,
              'Costo día/hora': item.costoMensual,
            }));
            const wsFijos = XLSX.utils.json_to_sheet(sheetFijos);
            XLSX.utils.book_append_sheet(wb, wsFijos, 'Costos Fijos');

            // Costos Variables
            const sheetVariables = costosVariables.map((item, idx) => ({
              '#': idx + 1,
              Concepto: item.concepto,
              'Costo día/hora': item.costoMensual,
            }));
            const wsVariables = XLSX.utils.json_to_sheet(sheetVariables);
            XLSX.utils.book_append_sheet(wb, wsVariables, 'Costos Variables');

            // Resultados
            const sheetResultados = [
              { Concepto: 'Costo Unitario', Valor: costoUnitario() },
              { Concepto: 'Costos Operativos', Valor: costosOperativos() },
              { Concepto: 'Costos U+O', Valor: costoUnitario() + costosOperativos() },
              { Concepto: 'Utilidad (%)', Valor: utilidad },
              { Concepto: 'Subtotal', Valor: subtotal() },
              { Concepto: 'IVA (%)', Valor: iva },
              { Concepto: 'TOTAL', Valor: total() },
            ];
            const wsResultados = XLSX.utils.json_to_sheet(sheetResultados);
            XLSX.utils.book_append_sheet(wb, wsResultados, 'Resultados');

            XLSX.writeFile(wb, `datos_calculo_productos_${empresaId}_${new Date().toISOString().slice(0, 10)}.xlsx`);
          }}
        >
          Descargar Excel (Todos los Datos)
        </button>
      </div>
      {/* Tabla Inventario */}
      <div className="box has-background-white">
        <div className="level">
          <div className="level-left">
            <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Inventario</h2>
          </div>
          <div className="level-right">
            <button className="button is-gold has-text-black is-medium" onClick={addInventarioRow}>
              <span className="icon"><i className="fas fa-plus"></i></span>
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table is-bordered is-striped compact-table costos-table">
            <thead>
              <tr className="has-background-grey-lighter">
                <th>N° de artículo</th>
                <th>Nombre del artículo</th>
                <th>Distribuidor</th>
                <th>Unidad</th>
                <th>Stock total</th>
                <th>Salida</th>
                <th>Stock final</th>
                <th>Costo unitario</th>
                <th>IVA (%)</th>
                <th>Precio unitario</th>
                <th>Precio stock total</th>
                <th>Precio stock final</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="has-text-black">
              {inventario.filter(item => item !== null && item !== undefined).map((item) => (
                <tr key={item.id}>
                  <td>
                    <input className="input is-small" value={item.numeroArticulo} onChange={(e) => handleInventarioChange(item.id, 'numeroArticulo', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" value={item.nombreArticulo || ''} onChange={(e) => handleInventarioChange(item.id, 'nombreArticulo', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" value={item.distribuidor} onChange={(e) => handleInventarioChange(item.id, 'distribuidor', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" value={item.unidad} onChange={(e) => handleInventarioChange(item.id, 'unidad', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.stockTotal === 0 ? '' : item.stockTotal} onChange={(e) => handleInventarioChange(item.id, 'stockTotal', e.target.value)} placeholder="0" />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.salida === 0 ? '' : item.salida} onChange={(e) => handleInventarioChange(item.id, 'salida', e.target.value)} placeholder="0" />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.stockFinal.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <input
                      className="input is-small"
                      type="number"
                      step="0.0001"
                      min="0"
                      value={item.costoUnitario === 0 ? '' : item.costoUnitario}
                      onChange={(e) => handleInventarioChange(item.id, 'costoUnitario', e.target.value)}
                      placeholder="0"
                    />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.iva === 0 ? '' : item.iva} onChange={(e) => handleInventarioChange(item.id, 'iva', e.target.value)} placeholder="0" />
                  </td>
                  <td>
                    <input
                      className="input is-small"
                      type="number"
                      step="0.0001"
                      value={item.precioUnitario}
                      readOnly
                    />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.precioStockTotal.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.precioStockFinal.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <button className="button is-danger is-small" onClick={() => removeInventarioRow(item.id)}>
                      <span className="icon"><i className="fas fa-trash"></i></span>
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="has-background-grey-lighter">
                <td colSpan="11" className="has-text-right has-text-weight-bold">Total Precio Stock Final:</td>
                <td className="has-text-weight-bold">${roundToTwo(inventario.reduce((sum, item) => sum + num(item.precioStockFinal), 0)).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Tabla Costos Directos */}
      <div className="box has-background-white">
        <div className="level">
          <div className="level-left">
            <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Costos Directos</h2>
          </div>
          <div className="level-right">
            <button
              className="button is-gold has-text-black is-medium"
              onClick={addDirectoRow}
            >
              <span className="icon"><i className="fas fa-plus"></i></span>
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table is-bordered is-striped compact-table costos-table">
            <thead>
              <tr className="has-background-grey-lighter">
                <th>Nombre del producto</th>
                <th>Nombre del artículo</th>
                <th>Cantidad</th>
                <th>Unidad</th>
                <th>Costo unitario</th>
                <th>Costo total</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="has-text-black">
              {costosDirectos.map((item, idx) => (
                <tr key={item.id}>
                  <td>
                    <input className="input is-small" value={item.nombreProducto || ''} onChange={(e) => handleDirectoChange(item.id, 'nombreProducto', e.target.value)} />
                  </td>
                  <td>
                    <select
                      className="input is-small"
                      value={item.nombreArticulo || ''}
                      onChange={(e) => handleDirectoChange(item.id, 'nombreArticulo', e.target.value)}
                    >
                      <option value="">Seleccionar del inventario local</option>
                      {inventario.filter(inv => inv.nombreArticulo).map(inv => (
                        <option key={inv.id} value={inv.nombreArticulo}>{inv.nombreArticulo}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.cantidad} onChange={(e) => handleDirectoChange(item.id, 'cantidad', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" value={item.unidad || ''} onChange={(e) => handleDirectoChange(item.id, 'unidad', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.costoUnitario.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.costoTotal.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <button className="button is-danger is-small" onClick={() => removeDirectoRow(item.id)}>
                      <span className="icon"><i className="fas fa-trash"></i></span>
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="has-background-grey-lighter">
                <td colSpan="5" className="has-text-right has-text-weight-bold">Total Costo Total:</td>
                <td className="has-text-weight-bold">${roundToTwo(costosDirectos.reduce((sum, item) => sum + num(item.costoTotal), 0)).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Tabla Mano de Obra */}
      <div className="box has-background-white">
        <div className="level">
          <div className="level-left">
            <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Mano de Obra</h2>
          </div>
          <div className="level-right">
            <button className="button is-gold has-text-black is-medium" onClick={addManoObraRow}>
              <span className="icon"><i className="fas fa-plus"></i></span>
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table is-bordered is-striped compact-table costos-table">
            <thead>
              <tr className="has-background-grey-lighter">
                <th>Personal</th>
                <th>Horas</th>
                <th>Costo/Hora</th>
                <th>Costo Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="has-text-black">
              {manoObra.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input className="input is-small" value={item.personal || ''} onChange={(e) => handleManoObraChange(item.id, 'personal', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.horas} onChange={(e) => handleManoObraChange(item.id, 'horas', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.costoHora} onChange={(e) => handleManoObraChange(item.id, 'costoHora', e.target.value)} />
                  </td>
                  <td>
                    <input className="input is-small" type="number" value={item.costoTotal.toFixed(2)} readOnly />
                  </td>
                  <td>
                    <button className="button is-danger is-small" onClick={() => removeManoObraRow(item.id)}>
                      <span className="icon"><i className="fas fa-trash"></i></span>
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="has-background-grey-lighter">
                <td colSpan="3" className="has-text-right has-text-weight-bold">Total Costo Total:</td>
                <td className="has-text-weight-bold">${roundToTwo(manoObra.reduce((sum, item) => sum + num(item.costoTotal), 0)).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Tabla Costos Fijos y Variables */}
      <div className="box has-background-white">
        <div className="level">
          <div className="level-left">
            <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Costos Fijos y Variables</h2>
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <h3 className="subtitle is-6">Costos Fijos</h3>
            <div className="level mb-2">
              <div className="level-left"></div>
              <div className="level-right">
                <button className="button is-gold has-text-black is-small" onClick={addFijoRow}>
                  <span className="icon"><i className="fas fa-plus"></i></span>
                </button>
              </div>
            </div>
            <table className="table is-bordered is-striped compact-table costos-table">
              <thead>
                <tr className="has-background-grey-lighter">
                  <th>Concepto</th>
                  <th>Costo día/hora</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="has-text-black">
                {costosFijos.map(item => (
                  <tr key={item.id}>
                    <td>
                      <input className="input is-small" value={item.concepto} onChange={(e) => handleFijoChange(item.id, 'concepto', e.target.value)} />
                    </td>
                    <td>
                      <input className="input is-small" type="number" value={item.costoMensual} onChange={(e) => handleFijoChange(item.id, 'costoMensual', e.target.value)} />
                    </td>
                    <td>
                      <button className="button is-danger is-small" onClick={() => removeFijoRow(item.id)}>
                        <span className="icon"><i className="fas fa-trash"></i></span>
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="has-background-grey-lighter">
                  <td className="has-text-right has-text-weight-bold">Total Costo día/hora:</td>
                  <td className="has-text-weight-bold">${roundToTwo(costosFijos.reduce((sum, item) => sum + num(item.costoMensual), 0)).toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="column">
            <h3 className="subtitle is-6">Costos Variables</h3>
            <div className="level mb-2">
              <div className="level-left"></div>
              <div className="level-right">
                <button className="button is-gold has-text-black is-small" onClick={addVariableRow}>
                  <span className="icon"><i className="fas fa-plus"></i></span>
                </button>
              </div>
            </div>
            <table className="table is-bordered is-striped compact-table costos-table">
              <thead>
                <tr className="has-background-grey-lighter">
                  <th>Concepto</th>
                  <th>Costo día/hora</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="has-text-black">
                {costosVariables.map(item => (
                  <tr key={item.id}>
                    <td>
                      <input className="input is-small" value={item.concepto} onChange={(e) => handleVariableChange(item.id, 'concepto', e.target.value)} />
                    </td>
                    <td>
                      <input className="input is-small" type="number" value={item.costoMensual} onChange={(e) => handleVariableChange(item.id, 'costoMensual', e.target.value)} />
                    </td>
                    <td>
                      <button className="button is-danger is-small" onClick={() => removeVariableRow(item.id)}>
                        <span className="icon"><i className="fas fa-trash"></i></span>
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="has-background-grey-lighter">
                  <td className="has-text-right has-text-weight-bold">Total Costo día/hora:</td>
                  <td className="has-text-weight-bold">${roundToTwo(costosVariables.reduce((sum, item) => sum + num(item.costoMensual), 0)).toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Tabla Resultados */}
      <div className="box has-background-white">
        <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Resultados</h2>
        <table className="table is-bordered is-striped compact-table costos-table">
          <tbody className="has-text-black">
            <tr>
              <td className="has-text-weight-bold">Costo Unitario</td>
              <td>${costoUnitario().toFixed(2)}</td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">Costos Operativos</td>
              <td>${costosOperativos().toFixed(2)}</td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">Costos U+O</td>
              <td>${(costoUnitario() + costosOperativos()).toFixed(2)}</td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">Utilidad (%)</td>
              <td>
                <input
                  className="input is-small"
                  type="number"
                  value={utilidad === 0 || utilidad === '0' ? '' : utilidad}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val === '.') val = '0.';
                    setUtilidad(val);
                  }}
                  style={{ width: 80, display: 'inline-block' }}
                  placeholder="0"
                />
              </td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">Subtotal</td>
              <td>${subtotal().toFixed(2)}</td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">IVA (%)</td>
              <td>
                <input
                  className="input is-small"
                  type="number"
                  value={iva === 0 || iva === '0' ? '' : iva}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val === '.') val = '0.';
                    setIva(val);
                  }}
                  style={{ width: 80, display: 'inline-block' }}
                  placeholder="0"
                />
              </td>
            </tr>
            <tr>
              <td className="has-text-weight-bold">TOTAL</td>
              <td>${total().toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Tabla de productos guardados */}
      <div className="box has-background-white">
        <h2 className="subtitle is-4 has-text-weight-bold has-text-darkblue">Productos</h2>
        <div className="mb-3 is-flex" style={{ gap: '12px', alignItems: 'center' }}>
          <label>Desde:
            <input type="date" className="input is-small ml-2" value={filtroFechaInicio || ''} onChange={(e) => setFiltroFechaInicio(e.target.value)} />
          </label>
          <label>Hasta:
            <input type="date" className="input is-small ml-2" value={filtroFechaFin || ''} onChange={(e) => setFiltroFechaFin(e.target.value)} />
          </label>
          <button className="button is-link is-small" onClick={fetchProductosFiltrados}>Filtrar</button>
          <button className="button is-light is-small" onClick={() => { setFiltroFechaInicio(''); setFiltroFechaFin(''); fetchProductosFiltrados(true); }}>Limpiar</button>
        </div>
        <div className="mb-2">
          <button
            className="button is-link is-small mr-2"
            onClick={async () => {
              // Descargar Excel
              const productos = (() => {
                const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].productosGuardados;
                const array = Array.isArray(raw) ? raw : [];
                return array.filter(p => p !== null && p !== undefined);
              })();
              const data = productos.map((p, idx) => ({
                N: idx + 1,
                Producto: p.nombreProducto,
                Total: p.total,
                Fecha: p.fecha ? p.fecha.split('T')[0] : 'Sin fecha',
              }));
              const ws = XLSX.utils.json_to_sheet(data);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Productos');
              XLSX.writeFile(wb, `productos_${empresaId}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            }}
          >
            Descargar Excel
          </button>
          <button
            className="button is-gold is-small"
            onClick={() => {
              // Descargar PDF
              const productos = (() => {
                const raw = datosPorEmpresa[empresaId] && datosPorEmpresa[empresaId].productosGuardados;
                const array = Array.isArray(raw) ? raw : [];
                return array.filter(p => p !== null && p !== undefined);
              })();
              const doc = new jsPDF();
              autoTable(doc, {
                head: [['#', 'Producto', 'Total', 'Fecha']],
                body: productos.map((p, idx) => [idx + 1, p.nombreProducto, `$${p.total.toFixed(2)}`, p.fecha ? p.fecha.split('T')[0] : 'Sin fecha']),
              });
              doc.save(`productos_${empresaId}_${new Date().toISOString().slice(0, 10)}.pdf`);
            }}
          >
            Descargar PDF
          </button>
        </div>
        <table className="table is-bordered is-striped compact-table costos-table">
          <thead>
            <tr className="has-background-grey-lighter">
              <th>#</th>
              <th>Producto</th>
              <th>Total</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              return productosGuardados.length === 0 ? (
                <tr><td colSpan="5" className="has-text-centered">No hay productos guardados</td></tr>
              ) : (
                productosGuardados.map((prod, idx) => {
                  return (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {prod.editando ? (
                          <input className="input is-small" value={prod.nombreProductoEdit || prod.nombreProducto} onChange={(e) => {
                            setDatosPorEmpresa(prev => {
                              const arr = [...prev[empresaId].productosGuardados];
                              const foundIdx = arr.findIndex(p => p && p.id === prod.id);
                              if (foundIdx !== -1) arr[foundIdx].nombreProductoEdit = e.target.value;
                              return { ...prev, [empresaId]: { ...prev[empresaId], productosGuardados: arr } };
                            });
                          }} />
                        ) : prod.nombreProducto}
                      </td>
                      <td>
                        {prod.editando ? (
                          <input className="input is-small" type="number" value={prod.totalEdit || prod.total} onChange={(e) => {
                            setDatosPorEmpresa(prev => {
                              const arr = [...prev[empresaId].productosGuardados];
                              const foundIdx = arr.findIndex(p => p && p.id === prod.id);
                              if (foundIdx !== -1) arr[foundIdx].totalEdit = e.target.value;
                              return { ...prev, [empresaId]: { ...prev[empresaId], productosGuardados: arr } };
                            });
                          }} />
                        ) : `$${prod.total.toFixed(2)}`}
                      </td>
                      <td>{prod.fecha ? prod.fecha.split('T')[0] : 'Sin fecha'}</td>
                      <td>
                        {prod.editando ? (
                          <>
                            <button className="button is-link is-small mr-1" onClick={async () => {
                              // PATCH al backend
                              await apiRequest(`/productos-calculados/${prod.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ nombre: prod.nombreProductoEdit, total: prod.totalEdit })
                              });
                              setDatosPorEmpresa(prev => {
                                const arr = [...prev[empresaId].productosGuardados];
                                const foundIdx = arr.findIndex(p => p && p.id === prod.id);
                                if (foundIdx !== -1) {
                                  arr[foundIdx].nombreProducto = prod.nombreProductoEdit;
                                  arr[foundIdx].total = Number(prod.totalEdit);
                                  arr[foundIdx].editando = false;
                                }
                                return { ...prev, [empresaId]: { ...prev[empresaId], productosGuardados: arr } };
                              });
                            }} title="Guardar">
                              <span className="icon"><i className="fas fa-save"></i></span>
                            </button>
                            <button className="button is-light is-small" onClick={() => {
                              setDatosPorEmpresa(prev => {
                                const arr = [...prev[empresaId].productosGuardados];
                                const foundIdx = arr.findIndex(p => p && p.id === prod.id);
                                if (foundIdx !== -1) arr[foundIdx].editando = false;
                                return { ...prev, [empresaId]: { ...prev[empresaId], productosGuardados: arr } };
                              });
                            }} title="Cancelar">
                              <span className="icon"><i className="fas fa-times"></i></span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="button is-link is-small mr-1" onClick={() => {
                              // Establecer el ID del producto en edición
                              setEditingProductId(prod.id);

                              // Cargar datos de inventario
                              if (prod.detalles && prod.detalles.inventario && prod.detalles.inventario.length > 0) {
                                setInventario(prod.detalles.inventario);
                              } else {
                                setInventario(defaultInventario);
                              }

                              // Cargar costos directos
                              if (prod.detalles && prod.detalles.costosDirectos && prod.detalles.costosDirectos.length > 0) {
                                setCostosDirectos(prod.detalles.costosDirectos);
                              } else {
                                setCostosDirectos([{ id: 1, nombreProducto: prod.nombreProducto || '', nombreArticulo: '', cantidad: 0, unidad: '', costoUnitario: 0, costoTotal: 0 }]);
                              }

                              // Cargar mano de obra
                              if (prod.detalles && prod.detalles.manoObra && prod.detalles.manoObra.length > 0) {
                                setManoObra(prod.detalles.manoObra);
                              } else {
                                setManoObra([{ id: 1, personal: '', horas: 0, costoHora: 0, costoTotal: 0 }]);
                              }

                              // Cargar costos fijos
                              if (prod.detalles && prod.detalles.costosFijos && prod.detalles.costosFijos.length > 0) {
                                setCostosFijos(prod.detalles.costosFijos);
                              } else {
                                setCostosFijos([{ id: 1, concepto: '', costoMensual: 0 }]);
                              }

                              // Cargar costos variables
                              if (prod.detalles && prod.detalles.costosVariables && prod.detalles.costosVariables.length > 0) {
                                setCostosVariables(prod.detalles.costosVariables);
                              } else {
                                setCostosVariables([{ id: 1, concepto: '', costoMensual: 0 }]);
                              }

                              // Cargar utilidad e IVA
                              setUtilidad(prod.detalles?.utilidad ?? '');
                              setIva(prod.detalles?.iva ?? '');

                              // Scroll al inicio de la página para ver las tablas
                              window.scrollTo({ top: 0, behavior: 'smooth' });

                              // Mostrar mensaje informativo
                              alert(`Editando producto: ${prod.nombreProducto}\n\nLos datos han sido cargados en las tablas de arriba.`);
                            }} title="Editar">
                              <span className="icon"><i className="fas fa-edit"></i></span>
                            </button>
                            <button className="button is-danger is-small" onClick={async () => {
                              // DELETE al backend
                              await apiRequest(`/productos-calculados/${prod.id}`, {
                                method: 'DELETE'
                              });
                              setDatosPorEmpresa(prev => {
                                const arr = [...prev[empresaId].productosGuardados];
                                const foundIdx = arr.findIndex(p => p && p.id === prod.id);
                                if (foundIdx !== -1) arr.splice(foundIdx, 1);
                                return { ...prev, [empresaId]: { ...prev[empresaId], productosGuardados: arr } };
                              });
                            }} title="Eliminar">
                              <span className="icon"><i className="fas fa-trash"></i></span>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              );
            })()}
          </tbody>
        </table>
      </div>
      {/* Guardar producto y registrar ingreso */}
      <div className="has-text-centered mt-4">
        {editingProductId && (
          <button
            className="button is-warning is-medium mr-2"
            onClick={() => {
              setEditingProductId(null);
              setInventario(defaultInventario);
              setCostosDirectos([{ id: 1, nombreProducto: '', nombreArticulo: '', cantidad: 0, unidad: '', costoUnitario: 0, costoTotal: 0 }]);
              setManoObra([{ id: 1, personal: '', horas: 0, costoHora: 0, costoTotal: 0 }]);
              setCostosFijos([{ id: 1, concepto: '', costoMensual: 0 }]);
              setCostosVariables([{ id: 1, concepto: '', costoMensual: 0 }]);
              setUtilidad('');
              setIva('');
            }}
          >
            Cancelar Edición
          </button>
        )}
        <button
          className="button is-link is-medium"
          onClick={async () => {
            const producto = (costosDirectos.length > 0 && costosDirectos[0] && costosDirectos[0].nombreProducto) ? costosDirectos[0].nombreProducto : '';
            const precio = total();
            if (!producto || precio <= 0) {
              alert('Debe ingresar un producto y calcular un total mayor a 0');
              return;
            }
            try {
              // Obtener empresaId desde contexto
              const empresaId = (empresaSeleccionada && empresaSeleccionada.id) || null;
              if (!editingProductId) {
                await apiRequest('/ingresos', {
                  method: 'POST',
                  body: JSON.stringify({
                    descripcion: `Ingreso por producto calculado: ${producto}`,
                    producto,
                    monto: precio,
                    empresaId,
                    fecha: new Date().toISOString(),
                  })
                });

                // Reducir stock del inventario por cada artículo utilizado
                for (const costoDirecto of costosDirectos) {
                  if (costoDirecto.nombreArticulo && num(costoDirecto.cantidad) > 0) {
                    try {
                      // Buscar el artículo en el inventario por nombre
                      const inventarioItem = inventario.find(inv => inv.nombreArticulo === costoDirecto.nombreArticulo);
                      if (inventarioItem) {
                        const nuevoStock = Math.max(0, num(inventarioItem.cantidadEnStock) - num(costoDirecto.cantidad));
                        await apiRequest(`/inventario/${inventarioItem.id}`, {
                          method: 'PATCH',
                          body: JSON.stringify({
                            cantidadEnStock: nuevoStock
                          })
                        });
                      }
                    } catch (error) {
                      console.error('Error actualizando stock del inventario:', error);
                      // No fallar el proceso completo por error en inventario
                    }
                  }
                }
              }
              // Guardar o actualizar producto en el backend
              const method = editingProductId ? 'PATCH' : 'POST';
              const url = editingProductId ? `/productos-calculados/${editingProductId}` : '/productos-calculados';
              const body = editingProductId ? {
                nombre: producto,
                total: precio,
                detalles: {
                  inventario,
                  costosDirectos,
                  manoObra,
                  costosFijos,
                  costosVariables,
                  utilidad,
                  iva,
                  resultados: {
                    subtotal: subtotal(),
                    ivaMonto: ivaMonto(),
                    total: total()
                  }
                }
              } : {
                nombre: producto,
                total: precio,
                detalles: {
                  inventario,
                  costosDirectos,
                  manoObra,
                  costosFijos,
                  costosVariables,
                  utilidad,
                  iva,
                  resultados: {
                    subtotal: subtotal(),
                    ivaMonto: ivaMonto(),
                    total: total()
                  }
                },
                empresaId: empresaId !== 'default' ? empresaId : undefined,
              };
              const resProductos = await apiRequest(url, {
                method,
                body: JSON.stringify(body)
              });
              if (resProductos.ok) {
                const updated = await resProductos.json();
                // Actualizar producto localmente para la tabla de productos guardados
                setDatosPorEmpresa(prev => {
                  const productosGuardados = prev[empresaId]?.productosGuardados || [];
                  const existingIdx = productosGuardados.findIndex(p => p && p.id === (editingProductId || updated.id));
                  if (existingIdx !== -1) {
                    productosGuardados[existingIdx] = {
                      id: updated.id,
                      nombreProducto: updated.nombre,
                      total: updated.total,
                      fecha: updated.fecha,
                    };
                  } else {
                    productosGuardados.push({
                      id: updated.id,
                      nombreProducto: updated.nombre,
                      total: updated.total,
                      fecha: updated.fecha,
                    });
                  }
                  const newState = {
                    ...prev,
                    [empresaId]: {
                      ...prev[empresaId],
                      productosGuardados,
                    }
                  };
                  if (!editingProductId) {
                    newState[empresaId] = {
                      ...newState[empresaId],
                      costosDirectos: [{ id: 1, nombreProducto: '', nombreArticulo: '', cantidad: 0, unidad: '', costoUnitario: 0, costoTotal: 0 }],
                      manoObra: [{ id: 1, personal: '', horas: 0, costoHora: 0, costoTotal: 0 }],
                      costosFijos: [{ id: 1, concepto: '', costoMensual: 0 }],
                      costosVariables: [{ id: 1, concepto: '', costoMensual: 0 }],
                      utilidad: '',
                      iva: '',
                    };
                  }
                  return newState;
                });
                setEditingProductId(null);
              } else {
                alert('Error al guardar el producto en el backend');
              }
              alert(editingProductId ? 'Producto actualizado' : 'Producto guardado e ingreso registrado');
            } catch (e) {
              alert('Error al guardar el producto o registrar el ingreso');
            }
          }}
        >
          {editingProductId ? 'Actualizar producto' : 'Guardar producto y registrar ingreso'}
        </button>
      </div>
    </div>
  );
};

export default CalculoProductos;
